import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Settings2, RotateCcw, GripVertical } from 'lucide-react';

/**
 * ColumnConfig Component
 * Allows users to show/hide columns and reorder them via drag-and-drop
 * 
 * Props:
 * - columns: Array of column definitions { key, label, ... }
 * - visibleColumns: Array of visible column keys (in display order)
 * - onColumnChange: Callback when columns change (newVisibleColumns) => void
 * - storageKey: localStorage key for persistence
 * - requiredColumns: Array of column keys that cannot be hidden (default: first column)
 */
const ColumnConfig = ({
  columns = [],
  visibleColumns,
  onColumnChange,
  storageKey,
  requiredColumns = []
}) => {
  // Store both visibility and order
  const [columnOrder, setColumnOrder] = useState([]);
  const [localVisible, setLocalVisible] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Initialize from props or localStorage
  useEffect(() => {
    const allKeys = columns.map(c => c.key);
    
    if (storageKey) {
      const savedOrder = localStorage.getItem(`${storageKey}_order`);
      const savedVisible = localStorage.getItem(storageKey);
      
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder);
          // Ensure all columns are included (in case new columns were added)
          const mergedOrder = [...parsedOrder.filter(k => allKeys.includes(k)), 
                              ...allKeys.filter(k => !parsedOrder.includes(k))];
          setColumnOrder(mergedOrder);
        } catch (e) {
          setColumnOrder(allKeys);
        }
      } else {
        setColumnOrder(allKeys);
      }
      
      if (savedVisible) {
        try {
          const parsedVisible = JSON.parse(savedVisible);
          setLocalVisible(parsedVisible);
          onColumnChange?.(parsedVisible);
        } catch (e) {
          setLocalVisible(allKeys);
          onColumnChange?.(allKeys);
        }
      } else {
        setLocalVisible(allKeys);
        onColumnChange?.(allKeys);
      }
    } else if (visibleColumns) {
      setLocalVisible(visibleColumns);
      setColumnOrder(allKeys);
    } else {
      setLocalVisible(allKeys);
      setColumnOrder(allKeys);
    }
  }, []);

  const handleToggle = (columnKey) => {
    // Don't allow hiding required columns
    if (requiredColumns.includes(columnKey) && localVisible.includes(columnKey)) {
      return;
    }

    let newVisible;
    if (localVisible.includes(columnKey)) {
      newVisible = localVisible.filter(k => k !== columnKey);
    } else {
      // Add to visible list, maintaining the current order
      newVisible = columnOrder.filter(k => 
        localVisible.includes(k) || k === columnKey
      );
    }

    setLocalVisible(newVisible);
    // Return columns in the correct order
    const orderedVisible = columnOrder.filter(k => newVisible.includes(k));
    onColumnChange?.(orderedVisible);
    
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(orderedVisible));
    }
  };

  const handleReset = () => {
    const allKeys = columns.map(c => c.key);
    setLocalVisible(allKeys);
    setColumnOrder(allKeys);
    onColumnChange?.(allKeys);
    
    if (storageKey) {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}_order`);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, key) => {
    setDraggedItem(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
    // Add a slight delay for visual feedback
    setTimeout(() => {
      if (e.target) e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.target) e.target.style.opacity = '1';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem === targetKey) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetKey);

    // Remove dragged item and insert at new position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setColumnOrder(newOrder);
    
    // Update visible columns with new order
    const orderedVisible = newOrder.filter(k => localVisible.includes(k));
    onColumnChange?.(orderedVisible);
    
    if (storageKey) {
      localStorage.setItem(`${storageKey}_order`, JSON.stringify(newOrder));
      localStorage.setItem(storageKey, JSON.stringify(orderedVisible));
    }
    
    setDraggedItem(null);
  };

  // Handle dropdown open/close - prevent close during drag
  const handleOpenChange = (open) => {
    if (!draggedItem) {
      setDropdownOpen(open);
    }
  };

  const hiddenCount = columns.length - localVisible.length;

  // Get columns in the current order
  const orderedColumns = columnOrder
    .map(key => columns.find(c => c.key === key))
    .filter(Boolean);

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid="column-config-btn">
          <Settings2 className="h-4 w-4 mr-2" />
          Columns
          {hiddenCount > 0 && (
            <span className="ml-2 bg-slate-200 text-slate-600 text-xs rounded-full px-2">
              {hiddenCount} hidden
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-64"
        onPointerDownOutside={(e) => {
          // Prevent closing when dragging outside
          if (draggedItem) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          // Prevent closing on any outside interaction during drag
          if (draggedItem) {
            e.preventDefault();
          }
        }}
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Configure Columns</span>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2 text-xs"
            onClick={handleReset}
            data-testid="column-config-reset"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-xs text-slate-500">
          Drag to reorder • Check to show/hide
        </div>
        <DropdownMenuSeparator />
        <div className="p-2 space-y-1 max-h-[350px] overflow-y-auto">
          {orderedColumns.map(column => {
            const isRequired = requiredColumns.includes(column.key);
            const isVisible = localVisible.includes(column.key);
            const isDragging = draggedItem === column.key;
            
            return (
              <div
                key={column.key}
                draggable={!isRequired}
                onDragStart={(e) => handleDragStart(e, column.key)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.key)}
                className={`
                  flex items-center gap-2 px-2 py-2 rounded border border-transparent
                  ${isDragging ? 'opacity-50 border-dashed border-blue-400 bg-blue-50' : ''}
                  ${!isRequired ? 'cursor-grab active:cursor-grabbing hover:bg-slate-50' : 'opacity-60'}
                  transition-colors
                `}
                data-testid={`column-item-${column.key}`}
              >
                {!isRequired && (
                  <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
                )}
                {isRequired && (
                  <div className="w-4" /> 
                )}
                <Checkbox
                  checked={isVisible}
                  onCheckedChange={() => handleToggle(column.key)}
                  disabled={isRequired && isVisible}
                  data-testid={`column-toggle-${column.key}`}
                  className="flex-shrink-0"
                />
                <span className="text-sm flex-1 truncate">{column.label}</span>
                {isRequired && (
                  <span className="text-xs text-slate-400 flex-shrink-0">Required</span>
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ColumnConfig;
